// src/components/Admin/SystemSettings/SystemSettings.tsx
import React, { useState, useEffect } from 'react';
import {
  Settings,
  Save,
  RefreshCw,
  Globe,
  Mail,
  Phone,
  MapPin,
  RotateCcw,
  CheckCircle,
  AlertCircle,
  Info,
  X,
} from 'lucide-react';
import { useForm } from 'react-hook-form';
import '../ManageAuctions/ManageAuctionsPro.css';
import './SystemSettingsPro.css';

/* ------------------  TYPES  ------------------ */
interface SystemConfig {
  siteName: string;
  siteDescription: string;
  contactEmail: string;
  contactPhone: string;
  address: string;
  timezone: string;
  currency: string;
  language: string;
}

/* ------------------  CONSTANTS  ------------------ */
const DEFAULT_CONFIG: SystemConfig = {
  siteName: 'AuctionPlatform Pro',
  siteDescription: 'Professional Auction Management Platform',
  contactEmail: 'admin@auctionplatform.com',
  contactPhone: '9999999999',
  address: 'Mumbai, Maharashtra, India',
  timezone: 'Asia/Kolkata',
  currency: 'INR',
  language: 'en',
};

const STORAGE_KEY = 'system_config';

/* ------------------  HELPERS  ------------------ */
const loadStored = (): SystemConfig => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? { ...DEFAULT_CONFIG, ...JSON.parse(raw) } : DEFAULT_CONFIG;
  } catch {
    return DEFAULT_CONFIG;
  }
};

const saveStored = (data: SystemConfig) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
};

/* ------------------  COMPONENT  ------------------ */
const SystemSettings: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isDirty },
  } = useForm<SystemConfig>({ defaultValues: loadStored() });

  /* keep form in sync if another tab changes storage */
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && e.newValue) {
        reset({ ...DEFAULT_CONFIG, ...JSON.parse(e.newValue) });
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [reset]);

  /* auto-dismiss the status banner */
  useEffect(() => {
    if (saveStatus === 'idle') return;
    const t = setTimeout(() => setSaveStatus('idle'), 4000);
    return () => clearTimeout(t);
  }, [saveStatus]);

  /* ------------------  HANDLERS  ------------------ */
  const onSubmit = async (data: SystemConfig) => {
    setIsLoading(true);
    setSaveStatus('idle');
    try {
      await new Promise((r) => setTimeout(r, 600)); // simulate API
      saveStored(data);
      setSaveStatus('success');
      reset(data); // mark clean
    } catch {
      setSaveStatus('error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDiscard = () => {
    reset(loadStored());
    setSaveStatus('idle');
  };

  const handleDefaults = () => {
    reset(DEFAULT_CONFIG, { keepDefaultValues: true });
    setSaveStatus('idle');
  };

  /* ------------------  VALIDATIONS  ------------------ */
  const validateEmail = (email: string) => {
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    const validDomains = ['com', 'in', 'org'];
    if (!emailRegex.test(email)) return 'Please enter a valid email address';
    const domain = email.split('.').pop()?.toLowerCase();
    if (!domain || !validDomains.includes(domain)) return 'Email must end with .com, .in, or .org';
    return true;
  };

  const fieldClass = (hasError: boolean) => `mau-input ${hasError ? 'mau-input-error' : ''}`;
  const FieldError: React.FC<{ message?: string }> = ({ message }) =>
    message ? (
      <p className="mau-field-error">
        <AlertCircle />
        {message}
      </p>
    ) : null;

  const descLen = watch('siteDescription')?.length || 0;
  const addrLen = watch('address')?.length || 0;

  /* ------------------  RENDER  ------------------ */
  return (
    <div className="mau-root">
      {/* ---------- Header ---------- */}
      <header className="mau-hero">
        <div>
          <h1 className="mau-title">
            <span className="mau-title-icon">
              <Settings />
            </span>
            System Settings
          </h1>
          <p className="mau-subtitle">Configure your platform's basic information, contact details and regional options</p>
        </div>
        <div className="mau-hero-actions">
          {isDirty && <span className="mau-unsaved">Unsaved changes</span>}
        </div>
      </header>

      {saveStatus === 'success' && (
        <div className="mau-banner mau-banner-success" role="status">
          <CheckCircle />
          <span>Settings saved successfully.</span>
          <button type="button" onClick={() => setSaveStatus('idle')} aria-label="Dismiss">
            <X />
          </button>
        </div>
      )}
      {saveStatus === 'error' && (
        <div className="mau-banner mau-banner-error" role="alert">
          <AlertCircle />
          <span>Error saving settings. Please try again.</span>
          <button type="button" onClick={() => setSaveStatus('idle')} aria-label="Dismiss">
            <X />
          </button>
        </div>
      )}

      <div className="mau-banner mau-banner-info" role="note">
        <Info />
        <span>These settings are saved in this browser only.</span>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className="mau-settings-stack">
          {/* ---------- General ---------- */}
          <section className="mau-card mau-set-card">
            <div className="mau-set-head">
              <span className="mau-set-icon">
                <Globe />
              </span>
              <div>
                <h2>General</h2>
                <p>How your platform is named and described</p>
              </div>
            </div>
            <div className="mau-set-body mau-set-grid">
              <div className="mau-field">
                <label htmlFor="siteName">
                  Site name<span className="mau-req">*</span>
                </label>
                <input
                  id="siteName"
                  type="text"
                  className={fieldClass(!!errors.siteName)}
                  placeholder="Enter your site name"
                  {...register('siteName', {
                    required: 'Site name is required',
                    minLength: { value: 3, message: 'Site name must be at least 3 characters' },
                    maxLength: { value: 50, message: 'Site name must not exceed 50 characters' },
                    pattern: {
                      value: /^[a-zA-Z0-9\s\-_]+$/,
                      message: 'Site name can only contain letters, numbers, spaces, hyphens, and underscores',
                    },
                  })}
                />
                <FieldError message={errors.siteName?.message} />
              </div>

              <div className="mau-field mau-field-wide">
                <label htmlFor="siteDescription">
                  Site description<span className="mau-req">*</span>
                </label>
                <textarea
                  id="siteDescription"
                  rows={3}
                  className={fieldClass(!!errors.siteDescription)}
                  placeholder="Describe your auction platform..."
                  {...register('siteDescription', {
                    required: 'Site description is required',
                    minLength: { value: 10, message: 'Description must be at least 10 characters' },
                    maxLength: { value: 500, message: 'Description must not exceed 500 characters' },
                  })}
                />
                {errors.siteDescription ? (
                  <FieldError message={errors.siteDescription.message} />
                ) : (
                  <span className="mau-hint">{descLen}/500 characters</span>
                )}
              </div>
            </div>
          </section>

          {/* ---------- Contact ---------- */}
          <section className="mau-card mau-set-card">
            <div className="mau-set-head">
              <span className="mau-set-icon">
                <Mail />
              </span>
              <div>
                <h2>Contact</h2>
                <p>Where users and partners can reach you</p>
              </div>
            </div>
            <div className="mau-set-body mau-set-grid">
              <div className="mau-field">
                <label htmlFor="contactEmail">
                  Contact email<span className="mau-req">*</span>
                </label>
                <div className="mau-input-icon">
                  <Mail />
                  <input
                    id="contactEmail"
                    type="email"
                    className={fieldClass(!!errors.contactEmail)}
                    placeholder="admin@example.com"
                    {...register('contactEmail', {
                      required: 'Contact email is required',
                      maxLength: { value: 100, message: 'Email must not exceed 100 characters' },
                      validate: validateEmail,
                    })}
                  />
                </div>
                <FieldError message={errors.contactEmail?.message} />
              </div>

              <div className="mau-field">
                <label htmlFor="contactPhone">
                  Contact phone<span className="mau-req">*</span>
                </label>
                <div className="mau-input-icon">
                  <Phone />
                  <input
                    id="contactPhone"
                    type="tel"
                    maxLength={10}
                    className={fieldClass(!!errors.contactPhone)}
                    placeholder="10-digit mobile number"
                    {...register('contactPhone', {
                      required: 'Contact phone is required',
                      pattern: { value: /^[6-9]\d{9}$/, message: 'Phone number must start with 6-9 and be 10 digits' },
                      minLength: { value: 10, message: 'Phone number must be exactly 10 digits' },
                      maxLength: { value: 10, message: 'Phone number must be exactly 10 digits' },
                    })}
                    onKeyPress={(e) => /[0-9]/.test(e.key) || e.preventDefault()}
                  />
                </div>
                <FieldError message={errors.contactPhone?.message} />
              </div>

              <div className="mau-field mau-field-wide">
                <label htmlFor="address">
                  Address<span className="mau-req">*</span>
                </label>
                <div className="mau-input-icon mau-input-icon-top">
                  <MapPin />
                  <textarea
                    id="address"
                    rows={2}
                    className={fieldClass(!!errors.address)}
                    placeholder="Enter your complete address..."
                    {...register('address', {
                      required: 'Address is required',
                      minLength: { value: 10, message: 'Address must be at least 10 characters' },
                      maxLength: { value: 300, message: 'Address must not exceed 300 characters' },
                    })}
                  />
                </div>
                {errors.address ? <FieldError message={errors.address.message} /> : <span className="mau-hint">{addrLen}/300 characters</span>}
              </div>
            </div>
          </section>

          {/* ---------- Regional ---------- */}
          <section className="mau-card mau-set-card">
            <div className="mau-set-head">
              <span className="mau-set-icon">
                <Settings />
              </span>
              <div>
                <h2>Regional</h2>
                <p>Time zone, currency and language</p>
              </div>
            </div>
            <div className="mau-set-body mau-set-grid mau-set-grid-3">
              <div className="mau-field">
                <label htmlFor="timezone">
                  Timezone<span className="mau-req">*</span>
                </label>
                <select id="timezone" className={fieldClass(!!errors.timezone)} {...register('timezone', { required: 'Timezone is required' })}>
                  <option value="Asia/Kolkata">Asia/Kolkata (IST)</option>
                </select>
                <FieldError message={errors.timezone?.message} />
              </div>

              <div className="mau-field">
                <label htmlFor="currency">
                  Currency<span className="mau-req">*</span>
                </label>
                <select id="currency" className={fieldClass(!!errors.currency)} {...register('currency', { required: 'Currency is required' })}>
                  <option value="INR">INR (₹) - Indian Rupee</option>
                  <option value="USD">USD ($) - US Dollar</option>
                </select>
                <FieldError message={errors.currency?.message} />
              </div>

              <div className="mau-field">
                <label htmlFor="language">
                  Language<span className="mau-req">*</span>
                </label>
                <select id="language" className={fieldClass(!!errors.language)} {...register('language', { required: 'Language is required' })}>
                  <option value="en">English</option>
                </select>
                <FieldError message={errors.language?.message} />
              </div>
            </div>
          </section>
        </div>

        {/* ---------- Action bar ---------- */}
        <div className="mau-savebar">
          <div className="mau-savebar-left">
            <button type="button" className="mau-btn" onClick={handleDefaults} disabled={isLoading}>
              <RotateCcw />
              Reset to defaults
            </button>
          </div>
          <div className="mau-savebar-right">
            <button type="button" className="mau-btn" onClick={handleDiscard} disabled={isLoading || !isDirty}>
              Discard changes
            </button>
            <button type="submit" className="mau-btn mau-btn-primary" disabled={isLoading || !isDirty}>
              {isLoading ? (
                <>
                  <RefreshCw className="mau-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save />
                  Save settings
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default SystemSettings;
